import { PostgrestResponse } from '@supabase/supabase-js';
import { IProdutosSubGrupos } from '../../../types/tables/IProdutosSubGrupos';
import { BaseMultiTenantService } from '../base/BaseMultiTenantService';

/**
 * Serviço para gerenciamento de subgrupos de produtos com isolamento multi-tenant
 * Gerencia subcategorização dos produtos (Refrigerantes, Sucos, Bolos, etc.)
 */
export class ProdutosSubGruposService extends BaseMultiTenantService<IProdutosSubGrupos> {
     constructor() {
          super('produtos_sub_grupos', ['descricao'], 'fk_id_organizacao');
     }

     /**
      * Busca subgrupos por grupo pai
      */
     async getByGrupo(organizationId: number, grupoId: number): Promise<PostgrestResponse<IProdutosSubGrupos[]>> {
          return await this.client
               .from(this.tableName)
               .select(
                    `
                    *,
                    produtos_grupo:fk_id_produto_grupo(*)
               `,
               )
               .eq(this.organizationIdField, organizationId)
               .eq('fk_id_produto_grupo', grupoId)
               .is('deleted_at', null)
               .order('descricao', { ascending: true });
     }

     /**
      * Busca subgrupos com contagem de produtos
      */
     async getAllWithProductCount(organizationId: number): Promise<PostgrestResponse<IProdutosSubGrupos[]>> {
          return await this.client
               .from(this.tableName)
               .select(
                    `
                    *,
                    produtos_grupo:fk_id_produto_grupo(*),
                    produtos_count:produtos(count)
               `,
               )
               .eq(this.organizationIdField, organizationId)
               .is('deleted_at', null)
               .order('descricao', { ascending: true });
     }

     /**
      * Cria subgrupos padrão para um grupo específico
      */
     async criarSubGruposPadrao(organizationId: number, grupoId: number, nomeGrupo: string, userId?: number): Promise<any[]> {
          const subgruposPorGrupo: { [key: string]: string[] } = {
               Bebidas: ['Refrigerantes', 'Sucos', 'Águas', 'Energéticos', 'Chás', 'Cafés'],
               Lanches: ['Sanduíches', 'Wraps', 'Torradas', 'Hambúrgueres'],
               Doces: ['Chocolates', 'Balas', 'Pirulitos', 'Gomas'],
               Salgados: ['Coxinhas', 'Pastel', 'Esfirra', 'Pão de Açúcar'],
               Frutas: ['Frutas Frescas', 'Salada de Frutas', 'Frutas Secas'],
               Refeições: ['Pratos Principais', 'Acompanhamentos', 'Saladas'],
               Sobremesas: ['Bolos', 'Pudins', 'Sorvetes', 'Mousses'],
          };

          const subgrupos = subgruposPorGrupo[nomeGrupo] || ['Diversos'];
          const resultados = [];

          for (const descricao of subgrupos) {
               try {
                    const resultado = await this.create(
                         {
                              fk_id_produto_grupo: grupoId,
                              descricao: descricao,
                         },
                         organizationId,
                         userId,
                    );
                    resultados.push(resultado);
               } catch (error) {
                    console.error(`Erro ao criar subgrupo ${descricao}:`, error);
               }
          }

          return resultados;
     }

     /**
      * Verifica se o subgrupo pode ser excluído
      */
     async canDelete(id: number, organizationId: number): Promise<{ canDelete: boolean; reason?: string }> {
          // Verificar se há produtos neste subgrupo
          const { data: produtos } = await this.client.from('produtos').select('id').eq('fk_id_produto_sub_grupo', id).eq('fk_id_organizacao', organizationId).is('deleted_at', null);

          if (produtos && produtos.length > 0) {
               return { canDelete: false, reason: 'Existem produtos vinculados a este subgrupo' };
          }

          return { canDelete: true };
     }

     /**
      * Move subgrupo para outro grupo pai
      */
     async moverParaGrupo(id: number, organizationId: number, novoGrupoId: number, userId?: number): Promise<PostgrestResponse<IProdutosSubGrupos[]>> {
          return await this.update(id, { fk_id_produto_grupo: novoGrupoId }, organizationId, userId);
     }
}

export const produtosSubGruposService = new ProdutosSubGruposService();
